import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export default function TransfersView({ 
  settings, 
  setSettings, 
  settingsSaving, 
  handleUpdateSettings 
}) {
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Transfer Pricing</h2>
        <p className="text-sm text-gray-600 mt-1">Configure airport transfer pricing</p>
      </div>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleUpdateSettings} className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Airport Transfer Pricing</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="transfer_price_one_way">One-Way Transfer Price (€)</Label>
                  <Input
                    id="transfer_price_one_way"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="50.00"
                    value={settings.transfer_price_one_way}
                    onChange={(e) => setSettings({ ...settings, transfer_price_one_way: e.target.value })}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Price for one-way airport transfer (arrival or departure)
                  </p>
                </div>

                <div>
                  <Label htmlFor="transfer_price_round_trip">Round-Trip Transfer Price (€)</Label>
                  <Input
                    id="transfer_price_round_trip"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="100.00"
                    value={settings.transfer_price_round_trip}
                    onChange={(e) => setSettings({ ...settings, transfer_price_round_trip: e.target.value })}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Price for round-trip airport transfer (arrival + departure)
                  </p>
                </div>
              </div>
            </div>

            {/* Airport Transfer Availability */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Airport Transfer Types Availability</h3>
              <p className="text-sm text-gray-600 mb-4">
                Enable or disable specific transfer types for guests during booking
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex-1">
                    <Label className="font-medium text-gray-900">Arrival Only Transfer</Label>
                    <p className="text-xs text-gray-600 mt-1">
                      Transfer from airport to hotel
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.enable_transfer_arrival}
                      onChange={(e) => setSettings({ ...settings, enable_transfer_arrival: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {/* Message for Arrival Only */}
                {settings.enable_transfer_arrival && (
                  <div className="ml-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <Label htmlFor="transfer_arrival_message" className="font-medium text-gray-900 mb-2 block">
                      Custom Message for Arrival Only
                    </Label>
                    <p className="text-xs text-gray-600 mb-3">
                      This message will be shown to guests when they select "Arrival Only" transfer
                    </p>
                    <textarea
                      id="transfer_arrival_message"
                      value={settings.transfer_arrival_message}
                      onChange={(e) => setSettings({ ...settings, transfer_arrival_message: e.target.value })}
                      className="w-full p-3 border border-amber-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                      rows="3"
                      placeholder="Enter a message for guests (e.g., information about departure arrangements)"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex-1">
                    <Label className="font-medium text-gray-900">Departure Only Transfer</Label>
                    <p className="text-xs text-gray-600 mt-1">
                      Transfer from hotel to airport
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.enable_transfer_departure}
                      onChange={(e) => setSettings({ ...settings, enable_transfer_departure: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex-1">
                    <Label className="font-medium text-gray-900">Round Trip Transfer</Label>
                    <p className="text-xs text-gray-600 mt-1">
                      Both arrival and departure transfers
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.enable_transfer_round_trip}
                      onChange={(e) => setSettings({ ...settings, enable_transfer_round_trip: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button 
                type="submit" 
                disabled={settingsSaving}
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
              >
                {settingsSaving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
